import { useMutation, useQuery } from '@tanstack/react-query'
import type { LabTestTemplateSingleResponse, LabTestTemplatesListResponse } from '@type/labTestTep.type'
import { AXIOS_INSTANCE } from '../api'
import { queryClient } from '../store/provider/TanStackProvider'

type Props = {
  page?: number
  limit?: number
  query?: string
  category?: string
  slug?: string
  id?: string
}

export const useLabTestTemp = (arg: Props = {}) => {
  const { page = 1, limit = 100, query, category, slug, id } = arg
  const { data, isLoading } = useQuery<LabTestTemplatesListResponse>({
    enabled: !id,
    queryKey: ['lab_test_temp', { page, limit, query, category, slug }],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<LabTestTemplatesListResponse>('/lab_test_templates', {
        params: {
          'page[number]': page,
          'page[size]': limit,
          ...(query && { query }),
          ...(category && { category }),
          ...(slug && { slug })
        }
      })
      return data
    }
  })

  const { data: singleLabTestTemp, isLoading: singleLabTestTempIsLoading } = useQuery<
    LabTestTemplateSingleResponse | undefined
  >({
    enabled: !!id,
    queryKey: ['lab_test_temp', { id }],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<LabTestTemplateSingleResponse>(`/lab_test_templates/${id as string}`)
      return data
    }
  })

  const { mutateAsync: createLabTestTemp } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post('/lab_test_templates', formVal, {
        headers: {
          'Content-Type': 'application/vnd.api+json'
        }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_test_temp'] })
    }
  })

  const { mutateAsync: updateLabTestTemp } = useMutation({
    mutationFn: async ({ id, formVal }: { id: string; formVal: any }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/lab_test_templates/${id}`, formVal, {
        headers: {
          'Content-Type': 'application/vnd.api+json'
        }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_test_temp'] })
      queryClient.invalidateQueries({ queryKey: ['lab_test_temp', { id }] })
    }
  })

  const { mutateAsync: softDeleteLabTestTemp } = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await AXIOS_INSTANCE.delete(`/lab_test_templates/${id}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_test_temp'] })
      queryClient.invalidateQueries({ queryKey: ['lab_test_temp', { id }] })
    }
  })

  return {
    data,
    pagination: data?.meta.pagination,
    isLoading,
    singleLabTestTemp,
    singleLabTestTempIsLoading,
    createLabTestTemp,
    updateLabTestTemp,
    softDeleteLabTestTemp
  }
}
